// src/components/admin/CompanySettings.js
import React, { useState, useEffect } from 'react';
import { Building, Building2, MapPin, CreditCard, Upload, X, Save, Image } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import companySettingsService from '../../services/companySettings.service';
import functionalStructureService from '../../services/functionalStructure.service';

const emptyEntityAddress = () => ({ street: '', city: '', state: '', zipCode: '', country: '' });

const CompanySettings = () => {
    const { theme } = useTheme();
    const [companyInfo, setCompanyInfo] = useState({
        name: '',
        industry: '',
        size: '',
        email: '',
        phone: '',
        website: '',
        taxId: '',
        registrationNumber: '',
        ceoName: '',
        hrEmail: '',
        branch: '',
        address: {
            street: '',
            city: '',
            state: '',
            zipCode: '',
            country: ''
        },
        bankDetails: {
            bankName: '',
            accountNumber: '',
            accountHolderName: '',
            ifscCode: '',
            swiftCode: '',
            branchName: ''
        },
        logo: null
    });

    const [logoPreview, setLogoPreview] = useState(null);
    const [saveSuccess, setSaveSuccess] = useState(false);
    const [loading, setLoading] = useState(false);
    // Sub-company (legal entity) branding. Names come from the org chart; this
    // screen only attaches a logo + address to each so payslips render them.
    const [legalEntities, setLegalEntities] = useState([]);

    // Load company settings from backend
    useEffect(() => {
        fetchCompanySettings();
    }, []);

    const fetchCompanySettings = async () => {
        const authToken = localStorage.getItem('authToken');

        // Load from localStorage first
        const savedSettings = localStorage.getItem('companySettings');
        if (savedSettings) {
            try {
                const parsed = JSON.parse(savedSettings);
                setCompanyInfo(parsed);
                if (parsed.logo) {
                    setLogoPreview(parsed.logo);
                }
            } catch (e) {
                console.error('Error loading company settings:', e);
            }
        }

        // Fetch from backend if authenticated
        if (!authToken) {
            //console.log('⚠️ No auth token, using localStorage settings');
            return;
        }

        try {
            setLoading(true);
            const settings = await companySettingsService.getSettings();

            if (settings && Object.keys(settings).length > 0) {
                setCompanyInfo(settings);
                if (settings.logo) {
                    setLogoPreview(settings.logo);
                }
                companySettingsService.saveSettingsToLocalStorage(settings);
            }
            await loadLegalEntities(settings?.legal_entities);
        } catch (error) {
            console.error('Error fetching company settings:', error);
        } finally {
            setLoading(false);
        }
    };

    // Entity names are owned by the org chart (Functional Structure) so they stay in
    // sync with employees.legal_entity. Saved branding is merged in by name; entities
    // removed from the chart simply drop off this screen.
    const loadLegalEntities = async (savedEntities) => {
        try {
            const data = await functionalStructureService.getActive();
            const names = (data?.structure_data?.legalEntities || data?.legalEntities || [])
                .map(entity => entity?.name)
                .filter(Boolean);

            const saved = Array.isArray(savedEntities) ? savedEntities : [];
            setLegalEntities(names.map(name => {
                const existing = saved.find(entity => entity?.name === name);
                return {
                    name,
                    logo: existing?.logo || null,
                    address: { ...emptyEntityAddress(), ...(existing?.address || {}) }
                };
            }));
        } catch (error) {
            console.error('Error loading legal entities:', error);
        }
    };

    const handleEntityLogoUpload = (name, e) => {
        const file = e.target.files[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onloadend = () => {
            setLegalEntities(prev => prev.map(entity =>
                entity.name === name ? { ...entity, logo: reader.result } : entity
            ));
        };
        reader.readAsDataURL(file);
    };

    const removeEntityLogo = (name) => {
        setLegalEntities(prev => prev.map(entity =>
            entity.name === name ? { ...entity, logo: null } : entity
        ));
    };

    const handleEntityAddressChange = (name, field, value) => {
        setLegalEntities(prev => prev.map(entity =>
            entity.name === name
                ? { ...entity, address: { ...entity.address, [field]: value } }
                : entity
        ));
    };

    const handleInputChange = (field, value) => {
        setCompanyInfo(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleAddressChange = (field, value) => {
        setCompanyInfo(prev => ({
            ...prev,
            address: {
                ...prev.address,
                [field]: value
            }
        }));
    };

    const handleBankChange = (field, value) => {
        setCompanyInfo(prev => ({
            ...prev,
            bankDetails: {
                ...prev.bankDetails,
                [field]: value
            }
        }));
    };

    const handleLogoUpload = (e) => {
        const file = e.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                const base64String = reader.result;
                setLogoPreview(base64String);
                setCompanyInfo(prev => ({
                    ...prev,
                    logo: base64String
                }));
            };
            reader.readAsDataURL(file);
        }
    };

    const removeLogo = () => {
        setLogoPreview(null);
        setCompanyInfo(prev => ({
            ...prev,
            logo: null
        }));
    };

    const handleSave = async () => {
        const authToken = localStorage.getItem('authToken');

        // legal_entities is saved as its own setting key and read back at payslip time
        const payload = { ...companyInfo, legal_entities: legalEntities };

        // Save to localStorage
        companySettingsService.saveSettingsToLocalStorage(payload);

        // Save to backend if authenticated
        if (authToken) {
            try {
                setLoading(true);
                await companySettingsService.updateSettings(payload);
            } catch (error) {
                console.error('Error saving to backend:', error);
            } finally {
                setLoading(false);
            }
        }

        setSaveSuccess(true);
        setTimeout(() => setSaveSuccess(false), 3000);
    };

    const isFormComplete = () => {
        return companyInfo.name &&
               companyInfo.email &&
               companyInfo.website &&
               companyInfo.ceoName &&
               companyInfo.hrEmail &&
               companyInfo.branch &&
               companyInfo.address.street &&
               companyInfo.address.city &&
               companyInfo.bankDetails.bankName &&
               companyInfo.bankDetails.accountNumber;
    };

    return (
        <div className={`min-h-screen ${theme === 'dark' ? 'bg-gray-900' : 'bg-gray-50'}`}>
            <div className="p-6 space-y-6">
                {/* Header */}
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className={`text-3xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                            Company Settings
                        </h1>
                        <p className={`text-sm mt-1 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                            Manage your company information and configuration
                        </p>
                    </div>
                    <button
                        onClick={handleSave}
                        className={`px-6 py-2.5 rounded-lg font-medium flex items-center gap-2 transition-colors ${
                            saveSuccess
                                ? 'bg-green-600 text-white'
                                : 'bg-blue-600 text-white hover:bg-blue-700'
                        }`}
                    >
                        <Save className="w-4 h-4" />
                        {saveSuccess ? 'Saved!' : 'Save Changes'}
                    </button>
                </div>

                {/* Completion Status */}
                {!isFormComplete() && (
                    <div className={`p-4 rounded-lg border ${
                        theme === 'dark'
                            ? 'bg-yellow-900/20 border-yellow-800 text-yellow-400'
                            : 'bg-yellow-50 border-yellow-200 text-yellow-800'
                    }`}>
                        <p className="text-sm font-medium">
                            ⚠️ Complete all required fields to enable payroll processing
                        </p>
                    </div>
                )}

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Company Logo */}
                    <div className={`rounded-xl shadow-sm p-6 ${
                        theme === 'dark' ? 'bg-gray-800 border border-gray-700' : 'bg-white'
                    }`}>
                        <div className="flex items-center gap-2 mb-4">
                            <Image className={`w-5 h-5 ${theme === 'dark' ? 'text-blue-400' : 'text-blue-600'}`} />
                            <h2 className={`text-lg font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                                Company Logo
                            </h2>
                        </div>

                        <div className="space-y-4">
                            {logoPreview ? (
                                <div className="relative">
                                    <img
                                        src={logoPreview}
                                        alt="Company Logo"
                                        className={`w-full h-48 object-contain rounded-lg ${
                                            theme === 'dark' ? 'bg-gray-700' : 'bg-gray-100'
                                        }`}
                                    />
                                    <button
                                        onClick={removeLogo}
                                        className="absolute top-2 right-2 p-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                                    >
                                        <X className="w-4 h-4" />
                                    </button>
                                </div>
                            ) : (
                                <div className={`border-2 border-dashed rounded-lg p-8 text-center ${
                                    theme === 'dark'
                                        ? 'border-gray-600 bg-gray-700/30'
                                        : 'border-gray-300 bg-gray-50'
                                }`}>
                                    <Upload className={`w-12 h-12 mx-auto mb-2 ${
                                        theme === 'dark' ? 'text-gray-400' : 'text-gray-400'
                                    }`} />
                                    <p className={`text-sm mb-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
                                        Upload your company logo
                                    </p>
                                    <label className="inline-block px-4 py-2 bg-blue-600 text-white rounded-lg cursor-pointer hover:bg-blue-700">
                                        Choose File
                                        <input
                                            type="file"
                                            accept="image/*"
                                            onChange={handleLogoUpload}
                                            className="hidden"
                                        />
                                    </label>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Basic Information */}
                    <div className={`rounded-xl shadow-sm p-6 ${
                        theme === 'dark' ? 'bg-gray-800 border border-gray-700' : 'bg-white'
                    }`}>
                        <div className="flex items-center gap-2 mb-4">
                            <Building className={`w-5 h-5 ${theme === 'dark' ? 'text-blue-400' : 'text-blue-600'}`} />
                            <h2 className={`text-lg font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                                Basic Information
                            </h2>
                        </div>
                        <div className="space-y-4">
                            <div>
                                <label className={`block text-sm font-medium mb-1 ${
                                    theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                                }`}>
                                    Company Name <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    value={companyInfo.name}
                                    onChange={(e) => handleInputChange('name', e.target.value)}
                                    className={`w-full px-3 py-2 border rounded-lg ${
                                        theme === 'dark'
                                            ? 'bg-gray-700 border-gray-600 text-white'
                                            : 'bg-white border-gray-300 text-gray-900'
                                    }`}
                                    placeholder="Enter company name"
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className={`block text-sm font-medium mb-1 ${
                                        theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                                    }`}>Industry</label>
                                    <select
                                        value={companyInfo.industry}
                                        onChange={(e) => handleInputChange('industry', e.target.value)}
                                        className={`w-full px-3 py-2 border rounded-lg ${
                                            theme === 'dark'
                                                ? 'bg-gray-700 border-gray-600 text-white'
                                                : 'bg-white border-gray-300 text-gray-900'
                                        }`}
                                    >
                                        <option value="">Select...</option>
                                        <option>Technology</option>
                                        <option>Healthcare</option>
                                        <option>Finance</option>
                                        <option>Education</option>
                                        <option>Manufacturing</option>
                                        <option>Retail</option>
                                    </select>
                                </div>
                                <div>
                                    <label className={`block text-sm font-medium mb-1 ${
                                        theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                                    }`}>Company Size</label>
                                    <select
                                        value={companyInfo.size}
                                        onChange={(e) => handleInputChange('size', e.target.value)}
                                        className={`w-full px-3 py-2 border rounded-lg ${
                                            theme === 'dark'
                                                ? 'bg-gray-700 border-gray-600 text-white'
                                                : 'bg-white border-gray-300 text-gray-900'
                                        }`}
                                    >
                                        <option value="">Select...</option>
                                        <option>1-50 employees</option>
                                        <option>51-200 employees</option>
                                        <option>201-500 employees</option>
                                        <option>500+ employees</option>
                                    </select>
                                </div>
                            </div>
                            <div>
                                <label className={`block text-sm font-medium mb-1 ${
                                    theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                                }`}>
                                    Email <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="email"
                                    value={companyInfo.email}
                                    onChange={(e) => handleInputChange('email', e.target.value)}
                                    className={`w-full px-3 py-2 border rounded-lg ${
                                        theme === 'dark'
                                            ? 'bg-gray-700 border-gray-600 text-white'
                                            : 'bg-white border-gray-300 text-gray-900'
                                    }`}
                                    placeholder="contact@company.com"
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className={`block text-sm font-medium mb-1 ${
                                        theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                                    }`}>Phone</label>
                                    <input
                                        type="tel"
                                        value={companyInfo.phone}
                                        onChange={(e) => handleInputChange('phone', e.target.value)}
                                        className={`w-full px-3 py-2 border rounded-lg ${
                                            theme === 'dark'
                                                ? 'bg-gray-700 border-gray-600 text-white'
                                                : 'bg-white border-gray-300 text-gray-900'
                                        }`}
                                        placeholder="+1-555-0100"
                                    />
                                </div>
                                <div>
                                    <label className={`block text-sm font-medium mb-1 ${
                                        theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                                    }`}>
                                        Website <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="url"
                                        value={companyInfo.website}
                                        onChange={(e) => handleInputChange('website', e.target.value)}
                                        className={`w-full px-3 py-2 border rounded-lg ${
                                            theme === 'dark'
                                                ? 'bg-gray-700 border-gray-600 text-white'
                                                : 'bg-white border-gray-300 text-gray-900'
                                        }`}
                                        placeholder="www.company.com"
                                    />
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className={`block text-sm font-medium mb-1 ${
                                        theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                                    }`}>
                                        CEO Name <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        value={companyInfo.ceoName}
                                        onChange={(e) => handleInputChange('ceoName', e.target.value)}
                                        className={`w-full px-3 py-2 border rounded-lg ${
                                            theme === 'dark'
                                                ? 'bg-gray-700 border-gray-600 text-white'
                                                : 'bg-white border-gray-300 text-gray-900'
                                        }`}
                                        placeholder="John Smith"
                                    />
                                </div>
                                <div>
                                    <label className={`block text-sm font-medium mb-1 ${
                                        theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                                    }`}>
                                        HR Email <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="email"
                                        value={companyInfo.hrEmail}
                                        onChange={(e) => handleInputChange('hrEmail', e.target.value)}
                                        className={`w-full px-3 py-2 border rounded-lg ${
                                            theme === 'dark'
                                                ? 'bg-gray-700 border-gray-600 text-white'
                                                : 'bg-white border-gray-300 text-gray-900'
                                        }`}
                                        placeholder="hr@company.com"
                                    />
                                </div>
                            </div>
                            <div>
                                <label className={`block text-sm font-medium mb-1 ${
                                    theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                                }`}>
                                    Branch / Office Location <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    value={companyInfo.branch}
                                    onChange={(e) => handleInputChange('branch', e.target.value)}
                                    className={`w-full px-3 py-2 border rounded-lg ${
                                        theme === 'dark'
                                            ? 'bg-gray-700 border-gray-600 text-white'
                                            : 'bg-white border-gray-300 text-gray-900'
                                    }`}
                                    placeholder="Head Office / Mumbai Branch"
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className={`block text-sm font-medium mb-1 ${
                                        theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                                    }`}>Tax ID</label>
                                    <input
                                        type="text"
                                        value={companyInfo.taxId}
                                        onChange={(e) => handleInputChange('taxId', e.target.value)}
                                        className={`w-full px-3 py-2 border rounded-lg ${
                                            theme === 'dark'
                                                ? 'bg-gray-700 border-gray-600 text-white'
                                                : 'bg-white border-gray-300 text-gray-900'
                                        }`}
                                        placeholder="XX-XXXXXXX"
                                    />
                                </div>
                                <div>
                                    <label className={`block text-sm font-medium mb-1 ${
                                        theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                                    }`}>Registration Number</label>
                                    <input
                                        type="text"
                                        value={companyInfo.registrationNumber}
                                        onChange={(e) => handleInputChange('registrationNumber', e.target.value)}
                                        className={`w-full px-3 py-2 border rounded-lg ${
                                            theme === 'dark'
                                                ? 'bg-gray-700 border-gray-600 text-white'
                                                : 'bg-white border-gray-300 text-gray-900'
                                        }`}
                                        placeholder="REG-XXXXXX"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Company Address */}
                    <div className={`rounded-xl shadow-sm p-6 ${
                        theme === 'dark' ? 'bg-gray-800 border border-gray-700' : 'bg-white'
                    }`}>
                        <div className="flex items-center gap-2 mb-4">
                            <MapPin className={`w-5 h-5 ${theme === 'dark' ? 'text-green-400' : 'text-green-600'}`} />
                            <h2 className={`text-lg font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                                Company Address
                            </h2>
                        </div>
                        <div className="space-y-4">
                            <div>
                                <label className={`block text-sm font-medium mb-1 ${
                                    theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                                }`}>
                                    Street Address <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    value={companyInfo.address.street}
                                    onChange={(e) => handleAddressChange('street', e.target.value)}
                                    className={`w-full px-3 py-2 border rounded-lg ${
                                        theme === 'dark'
                                            ? 'bg-gray-700 border-gray-600 text-white'
                                            : 'bg-white border-gray-300 text-gray-900'
                                    }`}
                                    placeholder="123 Main Street"
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className={`block text-sm font-medium mb-1 ${
                                        theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                                    }`}>
                                        City <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        value={companyInfo.address.city}
                                        onChange={(e) => handleAddressChange('city', e.target.value)}
                                        className={`w-full px-3 py-2 border rounded-lg ${
                                            theme === 'dark'
                                                ? 'bg-gray-700 border-gray-600 text-white'
                                                : 'bg-white border-gray-300 text-gray-900'
                                        }`}
                                        placeholder="New York"
                                    />
                                </div>
                                <div>
                                    <label className={`block text-sm font-medium mb-1 ${
                                        theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                                    }`}>State/Province</label>
                                    <input
                                        type="text"
                                        value={companyInfo.address.state}
                                        onChange={(e) => handleAddressChange('state', e.target.value)}
                                        className={`w-full px-3 py-2 border rounded-lg ${
                                            theme === 'dark'
                                                ? 'bg-gray-700 border-gray-600 text-white'
                                                : 'bg-white border-gray-300 text-gray-900'
                                        }`}
                                        placeholder="NY"
                                    />
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className={`block text-sm font-medium mb-1 ${
                                        theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                                    }`}>ZIP/Postal Code</label>
                                    <input
                                        type="text"
                                        value={companyInfo.address.zipCode}
                                        onChange={(e) => handleAddressChange('zipCode', e.target.value)}
                                        className={`w-full px-3 py-2 border rounded-lg ${
                                            theme === 'dark'
                                                ? 'bg-gray-700 border-gray-600 text-white'
                                                : 'bg-white border-gray-300 text-gray-900'
                                        }`}
                                        placeholder="10001"
                                    />
                                </div>
                                <div>
                                    <label className={`block text-sm font-medium mb-1 ${
                                        theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                                    }`}>Country</label>
                                    <input
                                        type="text"
                                        value={companyInfo.address.country}
                                        onChange={(e) => handleAddressChange('country', e.target.value)}
                                        className={`w-full px-3 py-2 border rounded-lg ${
                                            theme === 'dark'
                                                ? 'bg-gray-700 border-gray-600 text-white'
                                                : 'bg-white border-gray-300 text-gray-900'
                                        }`}
                                        placeholder="United States"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Bank Account Details */}
                    <div className={`rounded-xl shadow-sm p-6 ${
                        theme === 'dark' ? 'bg-gray-800 border border-gray-700' : 'bg-white'
                    }`}>
                        <div className="flex items-center gap-2 mb-4">
                            <CreditCard className={`w-5 h-5 ${theme === 'dark' ? 'text-purple-400' : 'text-purple-600'}`} />
                            <h2 className={`text-lg font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                                Bank Account Details
                            </h2>
                        </div>
                        <div className="space-y-4">
                            <div>
                                <label className={`block text-sm font-medium mb-1 ${
                                    theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                                }`}>
                                    Bank Name <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    value={companyInfo.bankDetails.bankName}
                                    onChange={(e) => handleBankChange('bankName', e.target.value)}
                                    className={`w-full px-3 py-2 border rounded-lg ${
                                        theme === 'dark'
                                            ? 'bg-gray-700 border-gray-600 text-white'
                                            : 'bg-white border-gray-300 text-gray-900'
                                    }`}
                                    placeholder="Bank of America"
                                />
                            </div>
                            <div>
                                <label className={`block text-sm font-medium mb-1 ${
                                    theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                                }`}>
                                    Account Holder Name <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    value={companyInfo.bankDetails.accountHolderName}
                                    onChange={(e) => handleBankChange('accountHolderName', e.target.value)}
                                    className={`w-full px-3 py-2 border rounded-lg ${
                                        theme === 'dark'
                                            ? 'bg-gray-700 border-gray-600 text-white'
                                            : 'bg-white border-gray-300 text-gray-900'
                                    }`}
                                    placeholder="Company Name"
                                />
                            </div>
                            <div>
                                <label className={`block text-sm font-medium mb-1 ${
                                    theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                                }`}>
                                    Account Number <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    value={companyInfo.bankDetails.accountNumber}
                                    onChange={(e) => handleBankChange('accountNumber', e.target.value)}
                                    className={`w-full px-3 py-2 border rounded-lg ${
                                        theme === 'dark'
                                            ? 'bg-gray-700 border-gray-600 text-white'
                                            : 'bg-white border-gray-300 text-gray-900'
                                    }`}
                                    placeholder="XXXXXXXXXXXX"
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className={`block text-sm font-medium mb-1 ${
                                        theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                                    }`}>IFSC Code</label>
                                    <input
                                        type="text"
                                        value={companyInfo.bankDetails.ifscCode}
                                        onChange={(e) => handleBankChange('ifscCode', e.target.value)}
                                        className={`w-full px-3 py-2 border rounded-lg ${
                                            theme === 'dark'
                                                ? 'bg-gray-700 border-gray-600 text-white'
                                                : 'bg-white border-gray-300 text-gray-900'
                                        }`}
                                        placeholder="XXXX0000000"
                                    />
                                </div>
                                <div>
                                    <label className={`block text-sm font-medium mb-1 ${
                                        theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                                    }`}>SWIFT Code</label>
                                    <input
                                        type="text"
                                        value={companyInfo.bankDetails.swiftCode}
                                        onChange={(e) => handleBankChange('swiftCode', e.target.value)}
                                        className={`w-full px-3 py-2 border rounded-lg ${
                                            theme === 'dark'
                                                ? 'bg-gray-700 border-gray-600 text-white'
                                                : 'bg-white border-gray-300 text-gray-900'
                                        }`}
                                        placeholder="XXXXXXXX"
                                    />
                                </div>
                            </div>
                            <div>
                                <label className={`block text-sm font-medium mb-1 ${
                                    theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                                }`}>Branch Name</label>
                                <input
                                    type="text"
                                    value={companyInfo.bankDetails.branchName}
                                    onChange={(e) => handleBankChange('branchName', e.target.value)}
                                    className={`w-full px-3 py-2 border rounded-lg ${
                                        theme === 'dark'
                                            ? 'bg-gray-700 border-gray-600 text-white'
                                            : 'bg-white border-gray-300 text-gray-900'
                                    }`}
                                    placeholder="Downtown Branch"
                                />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Legal Entities */}
                {legalEntities.length > 0 && (
                    <div className={`rounded-xl shadow-sm p-6 ${
                        theme === 'dark' ? 'bg-gray-800 border border-gray-700' : 'bg-white'
                    }`}>
                        <div className="flex items-center gap-2 mb-1">
                            <Building2 className={`w-5 h-5 ${theme === 'dark' ? 'text-blue-400' : 'text-blue-600'}`} />
                            <h2 className={`text-lg font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                                Legal Entities
                            </h2>
                        </div>
                        <p className={`text-sm mb-5 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                            Payslips for employees assigned to an entity use that entity's logo, name and
                            address. Anything left blank falls back to the company details above. Entities
                            are managed in Onboarding → Functional Structure.
                        </p>

                        <div className="space-y-5">
                            {legalEntities.map((entity) => (
                                <div
                                    key={entity.name}
                                    className={`rounded-lg border p-4 ${
                                        theme === 'dark' ? 'border-gray-700 bg-gray-700/20' : 'border-gray-200 bg-gray-50'
                                    }`}
                                >
                                    <h3 className={`text-sm font-semibold mb-4 ${
                                        theme === 'dark' ? 'text-white' : 'text-gray-900'
                                    }`}>
                                        {entity.name}
                                    </h3>

                                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                                        {/* Entity logo */}
                                        <div>
                                            <label className={`block text-sm font-medium mb-1 ${
                                                theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                                            }`}>Logo</label>
                                            {entity.logo ? (
                                                <div className="relative">
                                                    <img
                                                        src={entity.logo}
                                                        alt={`${entity.name} logo`}
                                                        className={`w-full h-32 object-contain rounded-lg ${
                                                            theme === 'dark' ? 'bg-gray-700' : 'bg-white'
                                                        }`}
                                                    />
                                                    <button
                                                        onClick={() => removeEntityLogo(entity.name)}
                                                        className="absolute top-2 right-2 p-1.5 bg-red-600 text-white rounded-lg hover:bg-red-700"
                                                    >
                                                        <X className="w-3.5 h-3.5" />
                                                    </button>
                                                </div>
                                            ) : (
                                                <div className={`border-2 border-dashed rounded-lg p-5 text-center h-32 flex flex-col items-center justify-center ${
                                                    theme === 'dark' ? 'border-gray-600' : 'border-gray-300 bg-white'
                                                }`}>
                                                    <Upload className="w-7 h-7 mb-2 text-gray-400" />
                                                    <label className="inline-block px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg cursor-pointer hover:bg-blue-700">
                                                        Choose File
                                                        <input
                                                            type="file"
                                                            accept="image/*"
                                                            onChange={(e) => handleEntityLogoUpload(entity.name, e)}
                                                            className="hidden"
                                                        />
                                                    </label>
                                                </div>
                                            )}
                                        </div>

                                        {/* Entity address */}
                                        <div className="space-y-3">
                                            <div>
                                                <label className={`block text-sm font-medium mb-1 ${
                                                    theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                                                }`}>Street Address</label>
                                                <input
                                                    type="text"
                                                    value={entity.address.street}
                                                    onChange={(e) => handleEntityAddressChange(entity.name, 'street', e.target.value)}
                                                    className={`w-full px-3 py-2 border rounded-lg ${
                                                        theme === 'dark'
                                                            ? 'bg-gray-700 border-gray-600 text-white'
                                                            : 'bg-white border-gray-300 text-gray-900'
                                                    }`}
                                                    placeholder="Registered office address"
                                                />
                                            </div>
                                            <div className="grid grid-cols-2 gap-3">
                                                <div>
                                                    <label className={`block text-sm font-medium mb-1 ${
                                                        theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                                                    }`}>City</label>
                                                    <input
                                                        type="text"
                                                        value={entity.address.city}
                                                        onChange={(e) => handleEntityAddressChange(entity.name, 'city', e.target.value)}
                                                        className={`w-full px-3 py-2 border rounded-lg ${
                                                            theme === 'dark'
                                                                ? 'bg-gray-700 border-gray-600 text-white'
                                                                : 'bg-white border-gray-300 text-gray-900'
                                                        }`}
                                                        placeholder="Hyderabad"
                                                    />
                                                </div>
                                                <div>
                                                    <label className={`block text-sm font-medium mb-1 ${
                                                        theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                                                    }`}>State</label>
                                                    <input
                                                        type="text"
                                                        value={entity.address.state}
                                                        onChange={(e) => handleEntityAddressChange(entity.name, 'state', e.target.value)}
                                                        className={`w-full px-3 py-2 border rounded-lg ${
                                                            theme === 'dark'
                                                                ? 'bg-gray-700 border-gray-600 text-white'
                                                                : 'bg-white border-gray-300 text-gray-900'
                                                        }`}
                                                        placeholder="Telangana"
                                                    />
                                                </div>
                                            </div>
                                            <div>
                                                <label className={`block text-sm font-medium mb-1 ${
                                                    theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                                                }`}>Postal Code</label>
                                                <input
                                                    type="text"
                                                    value={entity.address.zipCode}
                                                    onChange={(e) => handleEntityAddressChange(entity.name, 'zipCode', e.target.value)}
                                                    className={`w-full px-3 py-2 border rounded-lg ${
                                                        theme === 'dark'
                                                            ? 'bg-gray-700 border-gray-600 text-white'
                                                            : 'bg-white border-gray-300 text-gray-900'
                                                    }`}
                                                    placeholder="500081"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default CompanySettings;
